/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import InfiniteScroll from 'react-infinite-scroll-component';
import { actionCreators } from '../../reducers/templateEditor';
// eslint-disable-next-line max-len
import { actionCreators as fundsSelectorActionCreators } from '../../reducers/fundsSelectors';
import { StateShape as ReduxStateShape } from '../../reducers';
import { closeIcon } from '../../assets/images';
import FilterDropdown from './FilterDropdown';

interface Filter {
    label: string;
    labelKey: string;
    options: any;
    dropdownType?: 'Single Select' | 'Multi Select';
    analyticsLabel?: string;
}

const limit = 5;

const filtersData: Filter[] = [
    {
        label: 'Fund Type',
        labelKey: 'fundType',
        options: [],
        dropdownType: 'Multi Select',
    },
    {
        label: 'Risk Rating',
        labelKey: 'riskRating',
        options: [
            { text: 'High', value: '5' },
            { text: 'Medium to High', value: '4' },
            { text: 'Medium', value: '3' },
            { text: 'Low to Medium', value: '2' },
            { text: 'Low', value: '1' },
        ],
        dropdownType: 'Multi Select',
    },
    {
        label: 'MER',
        labelKey: 'mer',
        options: [
            { text: '<1%', value: '1' },
            { text: '1 to 2%', value: '2' },
            { text: '2 to 3%', value: '3' },
            { text: '3 to 4%', value: '4' },
            { text: '4 to 5%', value: '5' },
            { text: '>5%', value: '6' },
        ],
        dropdownType: 'Single Select',
    },
];

const FundsPickerLayer = () => {
    const { funds, hasMore, fundsLoading, selectedFunds, isSelectedAllFunds, fundsTypes } =
        useSelector((state: ReduxStateShape) => state.funds);
    const { isShowFundsPicker } = useSelector((state: ReduxStateShape) => state.templateEditor);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState(filtersData);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('fundName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [isShowResults, setIsShowResults] = useState(false);
    const dispatch = useDispatch();

    const [selectedValues, setSelectedValues] = useState<any>({});

    const handleSelect = (filterLabel: any, option: any) => {
        setSelectedValues((prevState: any) => ({
            ...prevState,
            [filterLabel]: option,
        }));
    };

    const handleMultiSelect = (filterLabel: any, option: any) => {
        setSelectedValues((prevState: any) => {
            const options = prevState[filterLabel as string] || [];
            if (options.find((o: any) => o.value === option.value)) {
                return {
                    ...prevState,
                    [filterLabel]: options.filter((o: any) => o.value !== option.value),
                };
            } else {
                return {
                    ...prevState,
                    [filterLabel]: [...options, option],
                };
            }
        });
    };

    const onClickClose = () => {
        dispatch(actionCreators.setIsShowFundsPicker(false));
        try {
            dispatch(fundsSelectorActionCreators.resetState(true));
        } catch (error) {
            console.error('Error', error);
        }
        setPage(1); // Reset page to 1 when search query changes
        setIsShowResults(false);
    };

    const onAddFunds = () => {
        dispatch(actionCreators.setIsShowFundsPicker(false));
        try {
            dispatch(fundsSelectorActionCreators.setSelectedFundsValue(selectedFunds.join(',')));
        } catch (error) {
            console.error('Error', error);
        }
        setPage(1); // Reset page to 1 when search query changes
        setIsShowResults(false);
    };

    const loadData = async (pageVal?: number) => {
        const transformedObject: any = {
            fundType: selectedValues.fundType?.map((item: { value: any }) => item.value),
            riskRating: selectedValues.riskRating?.map((item: { value: any }) =>
                Number(item?.value)
            ),
        };

        if (selectedValues?.mer?.value) {
            transformedObject.mer = [Number(selectedValues?.mer?.value)];
        }

        const pageNumber = pageVal || page;

        try {
            dispatch(
                fundsSelectorActionCreators.listFunds(
                    searchQuery,
                    pageNumber,
                    limit,
                    sortDirection,
                    sortColumn,
                    transformedObject
                )
            );
            if (!isShowResults) {
                setIsShowResults(true);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const onSelectFunds = (fundCode: string | null, isSelectAll?: boolean) => {
        dispatch(fundsSelectorActionCreators.setSelectedFunds(fundCode, isSelectAll));
    };

    const handleSearchChange = (e: any) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchKeyPress = (e: any) => {
        if (e.key === 'Enter') {
            try {
                dispatch(fundsSelectorActionCreators.resetState());
            } catch (error) {
                console.error('Error', error);
            }
            setPage(1); // Reset page to 1 when search query changes
            loadData(1);
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
        try {
            dispatch(fundsSelectorActionCreators.resetState());
        } catch (error) {
            console.error('Error', error);
        }
        setPage(1); // Reset page to 1 when sorting changes
    };

    const clearFilter = (labelKey: string, isMulti: boolean) => {
        const values = selectedValues;
        if (isMulti) {
            setSelectedValues({ ...values, [labelKey]: [] });
        } else {
            delete values[labelKey];
            setSelectedValues({ ...values });
        }
    };

    useEffect(() => {
        if (searchQuery || funds.length) {
            loadData();
        }
    }, [page, sortColumn, sortDirection]);

    useEffect(() => {
        if (!fundsTypes.length) {
            dispatch(fundsSelectorActionCreators.getFundsTypes());
        }
        if (fundsTypes.length) {
            const types = fundsTypes.map((d) => ({ text: d, value: d }));
            const _filters = filtersData.map((d) =>
                d.labelKey === 'fundType' ? { ...d, options: types } : { ...d }
            );
            setFilters(_filters);
        }
    }, [fundsTypes]);

    const loadMoreData = () => {
        setPage((prev) => prev + 1);
    };

    return (
        <Modal
            data-testid="modal"
            show={isShowFundsPicker}
            onHide={onClickClose}
            size="lg"
            style={{ top: '50px' }}
        >
            <div className="funds-picker-layer">
                <div className="search-input-container">
                    <div className="search-filters-input">
                        <div className="input-container">
                            <div className="left-section">
                                <div className="icon">
                                    <i className="icon-search"></i>
                                </div>
                            </div>
                            <input
                                placeholder="Search funds by code or name"
                                aira-label="Search funds by code or name"
                                type="text"
                                className="fund-search-input"
                                id="search-input"
                                onChange={handleSearchChange}
                                onKeyPress={handleSearchKeyPress}
                                value={searchQuery}
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className="primary button"
                        onClick={() => {
                            try {
                                dispatch(fundsSelectorActionCreators.resetState());
                            } catch (error) {
                                console.error('Error', error);
                            }
                            setPage(1); // Reset page to 1 when search query changes
                            loadData(page);
                        }}
                    >
                        Search Funds
                    </button>
                </div>
                <div className="filters-container">
                    {filters?.map((filter: any, i: number) => (
                        <div className={`filter dropdown-filter`} key={i}>
                            <FilterDropdown
                                options={filter.options}
                                label={filter?.label || ''}
                                labelKey={filter.labelKey}
                                dropdownType={
                                    filter?.dropdownType === ''
                                        ? 'Single Select'
                                        : filter?.dropdownType
                                }
                                clearFilter={clearFilter}
                                selectedValues={selectedValues}
                                handleSelect={handleSelect}
                                handleMultiSelect={handleMultiSelect}
                            />
                        </div>
                    ))}
                </div>
                <div className={'clear-filters'}>
                    <div className={'selected-funds-cont'}>
                        {selectedFunds.map((d) => (
                            <span>
                                <span>{d}</span>
                                <button>
                                    <img
                                        className={'close-icon'}
                                        onClick={() => onSelectFunds(d)}
                                        src={closeIcon}
                                        alt={`delete`}
                                    />
                                </button>
                            </span>
                        ))}
                    </div>
                    <button
                        type="button"
                        className={`secondary button ${
                            Object.keys(selectedValues)?.length ? '' : 'disabled'
                        }`}
                        onClick={() => setSelectedValues({})}
                    >
                        {`Clear Filters (${Object.keys(selectedValues)?.length})`}
                    </button>
                </div>
                {isShowResults ? (
                    <div
                        className={`search-results-container ${funds?.length ? '' : 'no-results'}`}
                    >
                        {funds?.length ? (
                            <>
                                <InfiniteScroll
                                    dataLength={funds.length}
                                    next={loadMoreData}
                                    hasMore={hasMore}
                                    loader={
                                        fundsLoading ? (
                                            <div className="loader-1 center">
                                                <span></span>
                                            </div>
                                        ) : (
                                            <div className={'loading-bar'}></div>
                                        )
                                    }
                                    endMessage={<p className={'end-of-list'}>End of list</p>}
                                    scrollableTarget="scrollableDiv"
                                >
                                    <div
                                        id="scrollableDiv"
                                        style={{ height: '200px', overflowY: 'scroll' }}
                                    >
                                        <table className="styled-table">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelectedAllFunds}
                                                            onChange={(e) => {
                                                                onSelectFunds(
                                                                    null,
                                                                    e.target.checked
                                                                );
                                                            }}
                                                        />
                                                    </th>
                                                    <th onClick={() => handleSort('fundName')}>
                                                        Fund Name
                                                    </th>
                                                    <th onClick={() => handleSort('nameSpace')}>
                                                        Name Space
                                                    </th>
                                                    <th onClick={() => handleSort('fundType')}>
                                                        Fund Type
                                                    </th>
                                                    <th onClick={() => handleSort('riskRating')}>
                                                        Risk Rating
                                                    </th>
                                                    <th onClick={() => handleSort('mer')}>MER</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {funds.map((fund) => (
                                                    <tr>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedFunds.includes(
                                                                    fund.fundCode
                                                                )}
                                                                onChange={() =>
                                                                    onSelectFunds(fund.fundCode)
                                                                }
                                                            />
                                                        </td>
                                                        <td>{fund.fundName}</td>
                                                        <td>{fund.nameSpace}</td>
                                                        <td>{fund.fundType}</td>
                                                        <td>{fund.riskRatingText}</td>
                                                        <td>{fund.merText}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </InfiniteScroll>
                            </>
                        ) : fundsLoading ? (
                            <div className="loader-1 center">
                                <span></span>
                            </div>
                        ) : (
                            <div className="no-results-container">
                                <p className="no-results-title">No search results</p>
                                <p className="no-results-message">
                                    Try using different search options.
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <></>
                )}
                <div className={'footer-buttons-container'}>
                    <button type="button" className="secondary button" onClick={onClickClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`primary button ${
                            !(selectedFunds?.length > 0) ? 'disabled' : ''
                        }`}
                        disabled={!(selectedFunds?.length > 0)}
                        onClick={onAddFunds}
                    >
                        Add Funds
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default FundsPickerLayer;
